<template>
  <div class="block mx-auto">
    <div class="bg-grey-background-subdued sm:bg-white sm:box-shadow-sm p-16">
      <p class="text-overline text-grey-header">You're in!</p>
      <div class="flex gap-16 mt-16">
        <ProgressBar :value="1" :max="1" content="You're in!" />
        <ProgressBar :value="1" :max="1" content="" />
      </div>
    </div>
    <div class="mx-auto lg:max-w-[986px]">
      <h1 class="text-h1 mt-24 mb-24 lg:mb-48 px-16">You’re ready to start applying to jobs</h1>
      <div class="flex flex-col-reverse gap-[24px] lg:flex-row lg:gap-[48px]">
        <div class="lg:w-1/2 px-16">
          <h3 class="text-h4">Take the first step to land a job</h3>
          <ImageLinkCard
            class="mt-16"
            title="Add a profile photo"
            content="Putting a face to your names helps build trust with families"
            :elevation="2"
            linkURL="/sitter/photo_upload#/sitter_upload"
            clickable
          >
            <template #rightSide>
              <DisclosureIcon class="mr-0 ml-16" />
            </template>
          </ImageLinkCard>
          <h3 class="text-grey-header text-overline mt-32 mb-16">Other ways to get started</h3>
          <ImageLinkCard
            class="mt-16"
            title="Apply to 10 or more jobs"
            content="Sitters who apply to 10 or more jobs are 4x more likely to get hired"
            :elevation="1"
            :linkURL="jobSearchLink"
            clickable
          >
            <template #rightSide>
              <DisclosureIcon class="mr-0 ml-16" />
            </template>
          </ImageLinkCard>
          <ImageLinkCard
            class="mt-16"
            title="Make yourself stand out"
            content="Personalize your profile with your education, references, and more"
            :elevation="1"
            :linkURL="profileEditLink"
            clickable
          >
            <template #rightSide>
              <DisclosureIcon class="mr-0 ml-16" />
            </template>
          </ImageLinkCard>
        </div>
        <div v-if="hasCardData" class="sitter-card-box lg:w-1/2">
          <div class="mx-16 mt-16 mb-24 md:mb-56">
            <p class="flex text-body mb-12">
              What your profile looks like to families
              <curving-arrow-icon class="relative bottom-[-10px] ml-4" />
            </p>
            <SitterCard
              :name="sitterCardName"
              :yearsExperience="yearsExperience"
              :city="city"
              :stateAbbr="stateAbbr"
              :description="bio"
              :hasCpr="hasCpr"
              :hasFirstAid="hasFirstAid"
              hasIdv
              :minRate="minRate"
              :maxRate="maxRate"
              photoUploadAvatar
              customClasses="max-w-[375px]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import snakeCase from 'lodash/snakeCase';
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import SitterCard from '@/components/sitter_card/SitterCard.vue';
import { Account } from '@/lib/public_api/Account';
import ImageLinkCard from '@/components/image_link_card/ImageLinkCard.vue';
import DisclosureIcon from '@/graphics/icons/DisclosureIcon.vue';
import OnboardingQueries from '@/graphql/onboarding/queries';
import CurvingArrowIcon from '@/graphics/icons/CurvingArrowIcon.vue';
import { sendSegmentEvent } from '@/lib/segment';
import { readDomainCookie, deleteDomainCookie } from '@/lib/domain_cookie';

export default {
  name: 'ProfileStepsComplete',
  components: {
    ProgressBar,
    SitterCard,
    ImageLinkCard,
    DisclosureIcon,
    CurvingArrowIcon,
  },
  props: {
    account: {
      required: true,
      type: Account,
    },
  },
  data(): any {
    return {
      zipCode: '',
      stateAbbr: '',
      city: '',
    };
  },
  computed: {
    yearsExperience(): Number {
      try {
        return this.$store.getters.getFormField('yearsExperience');
      } catch {
        return 0;
      }
    },
    careType(): String {
      return this.$store.getters.careType || 'babysitter';
    },
    sanitizedCareType(): String {
      return snakeCase(this.$store.getters.careType) || 'babysitter';
    },
    bio(): String {
      try {
        return this.$store.getters.getFormField('bio');
      } catch {
        return '';
      }
    },
    hasCpr(): boolean {
      try {
        return this.$store.getters.getFormField('qualifications').includes('CPR_CERTIFIED');
      } catch {
        return false;
      }
    },
    hasFirstAid(): boolean {
      try {
        return this.$store.getters.getFormField('qualifications').includes('FIRST_AID_TRAINING');
      } catch {
        return false;
      }
    },
    minRate(): Number {
      try {
        return this.$store.getters.minPaymentRate * 100;
      } catch {
        return 0;
      }
    },
    maxRate(): Number {
      try {
        return this.$store.getters.maxPaymentRate * 100;
      } catch {
        return 0;
      }
    },
    sitterCardName(): String {
      return `${this.account.firstName} ${this.account.lastName[0]}`;
    },
    profileEditLink(): String {
      return `/profile/provider/${this.sanitizedCareType}/edit_entry`;
    },
    jobSearchLink(): String {
      return `/jobs/search?search_strategy=${this.sanitizedCareType}_job&zipcode=${this.zipCode}`;
    },
    hasCardData(): Boolean {
      return this.$store.state.sitterOnboarding && this.account && this.bio;
    },
  },
  mounted() {
    this.fetchSitterLocation();

    if (readDomainCookie('profile_completed')) {
      this.trackSitterProfileCompleted();
    }
  },
  methods: {
    fetchSitterLocation() {
      this.$apollo
        .query({
          query: OnboardingQueries.FETCH_SITTER_LOCATION,
        })
        .then(({ data }) => {
          this.stateAbbr = data.viewer.location.stateabbr;
          this.city = data.viewer.location.cityname;
          this.zipCode = data.viewer.location.zipcode;
        })
        .catch((error) => {
          console.log(error.message); // eslint-disable-line no-console
        });
    },
    trackSitterProfileCompleted() {
      deleteDomainCookie('profile_completed');
      sendSegmentEvent('Sitter Profile Completed', {
        profile_type: this.careType,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sitter-card-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #ffeef8;
  background-image: url('@/images/account/pink_swoop.svg');
  background-size: 100% 90%;
  background-repeat: no-repeat;
  background-position: bottom;
  min-height: 349px;
  border-radius: 4px;
}
</style>
